import { useEffect, useState, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import { Button } from "../ui/button";
import {
  Wallet,
  CreditCard,
  Loader2,
  ShieldCheck,
  AlertTriangle,
  IndianRupee,
  CheckCircle2,
  Truck,
} from "lucide-react";
import { toast } from "sonner";
import api from "../../services/api";

/**
 * Phase 2, P1 — Customer Post-Order Pay-Now dialog.
 *
 * Two real flows handled by the same UI shell (label switches via
 * `flow` returned from the backend `pay-now/options`):
 *   • cod_to_prepaid — original booking was COD; customer wants to
 *     pay online so the agent doesn't have to collect cash.
 *   • prepaid_retry   — original booking was prepaid but the Razorpay
 *     attempt failed at checkout; customer is retrying.
 *
 * Wallet path: atomic deduct (HTTP 402 on insufficient balance).
 * Razorpay path: opens checkout, verifies signature server-side,
 * marks the order paid and flips `payment_method` to `prepaid_*`.
 *
 * Operational guard: backend returns `eligible=false` (HTTP 409) once
 * the order reaches `picked_up` / `in_transit` / etc. — frontend
 * shows the block reason instead of the payment buttons.
 *
 * Props
 *   open         : bool (controlled)
 *   onOpenChange : (bool) => void
 *   trackingId   : string
 *   onPaid       : (freshOrder) => void  — fires after success so the
 *                  parent can refresh status badges / totals.
 */
const FLOW_TITLE = {
  cod_to_prepaid: "Switch to prepaid",
  prepaid_retry: "Retry payment",
  pay_now: "Pay now",
};
const FLOW_BLURB = {
  cod_to_prepaid:
    "Pay online now so the delivery agent doesn't have to collect cash on pickup.",
  prepaid_retry:
    "Your last payment didn't go through. Retry below to complete your booking.",
  pay_now: "Settle this order online.",
};

export default function PayNowDialog({ open, onOpenChange, trackingId, onPaid }) {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null); // "wallet" | "razorpay" | null
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [done, setDone] = useState(null);

  const fetchOptions = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const res = await api.get(`/orders/${trackingId}/pay-now/options`);
      setData(res.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not load payment options");
    } finally {
      setLoading(false);
    }
  }, [trackingId]);

  useEffect(() => {
    if (!open || !trackingId) return;
    setDone(null);
    fetchOptions();
  }, [open, trackingId, fetchOptions]);

  const payWithWallet = async () => {
    setBusy("wallet");
    setErr(null);
    try {
      const res = await api.post(`/orders/${trackingId}/pay-now/wallet`);
      toast.success(res.data?.message || "Paid from wallet");
      setDone(res.data);
      if (onPaid) onPaid(res.data?.order);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Wallet payment failed";
      setErr(detail);
      toast.error(detail);
    } finally {
      setBusy(null);
    }
  };

  const ensureRazorpayScript = () =>
    new Promise((resolve, reject) => {
      if (window.Razorpay) return resolve();
      const id = "razorpay-script";
      if (document.getElementById(id)) {
        const wait = setInterval(() => {
          if (window.Razorpay) {
            clearInterval(wait);
            resolve();
          }
        }, 100);
        setTimeout(() => {
          clearInterval(wait);
          reject(new Error("Razorpay script took too long to load"));
        }, 8000);
        return;
      }
      const script = document.createElement("script");
      script.id = id;
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error("Failed to load Razorpay"));
      document.body.appendChild(script);
    });

  const payWithRazorpay = async () => {
    setBusy("razorpay");
    setErr(null);
    try {
      const { data: rz } = await api.post(`/orders/${trackingId}/pay-now/razorpay-order`);
      if (!rz?.razorpay_order_id || !rz?.key_id) {
        throw new Error("Razorpay not configured on the server");
      }
      await ensureRazorpayScript();

      const rzp = new window.Razorpay({
        key: rz.key_id,
        amount: rz.amount,
        currency: rz.currency || "INR",
        name: "PickNGo",
        description: `Order ${trackingId}`,
        order_id: rz.razorpay_order_id,
        handler: async (response) => {
          try {
            const verify = await api.post(
              `/orders/${trackingId}/pay-now/razorpay-verify`,
              {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
              },
            );
            toast.success(verify.data?.message || "Payment verified");
            setDone(verify.data);
            if (onPaid) onPaid(verify.data?.order);
          } catch (e) {
            const detail = e?.response?.data?.detail || "Could not verify payment";
            setErr(detail);
            toast.error(detail);
          } finally {
            setBusy(null);
          }
        },
        modal: {
          ondismiss: () => setBusy(null),
        },
        theme: { color: "#FF6B00" },
      });
      rzp.on("payment.failed", (resp) => {
        const reason = resp?.error?.description || "Payment failed";
        setErr(reason);
        toast.error(reason);
        setBusy(null);
      });
      rzp.open();
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Could not start payment";
      setErr(detail);
      toast.error(detail);
      setBusy(null);
    }
  };

  const amount = data?.amount_due_inr || 0;
  const walletBalance = data?.wallet_balance_inr || 0;
  const walletSufficient = !!data?.wallet_sufficient;
  const razorpayEnabled = !!data?.razorpay_enabled;
  const eligible = !!data?.eligible;
  const blockedReason = data?.blocked_reason || "";
  const flow = data?.flow || "pay_now";
  const title = FLOW_TITLE[flow] || FLOW_TITLE.pay_now;
  const blurb = FLOW_BLURB[flow] || FLOW_BLURB.pay_now;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="pay-now-dialog">
        <DialogHeader>
          <DialogTitle className="font-['Outfit'] text-xl font-bold flex items-center gap-2">
            <IndianRupee className="h-5 w-5 text-[#FF6B00]" />
            {title}
          </DialogTitle>
          <DialogDescription>{blurb}</DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="flex items-center justify-center py-10" data-testid="pay-now-loading">
            <Loader2 className="h-5 w-5 animate-spin text-[#FF6B00]" />
            <span className="ml-3 text-sm text-slate-500">Loading…</span>
          </div>
        )}

        {!loading && done && (
          <div
            className="p-5 rounded-xl bg-emerald-50 border border-emerald-200 text-center"
            data-testid="pay-now-success"
          >
            <CheckCircle2 className="h-10 w-10 text-emerald-600 mx-auto mb-2" />
            <p className="font-bold text-emerald-800">{done.message}</p>
            <p className="text-xs text-emerald-700 mt-1">
              Ref: <span className="font-mono">{done.transaction_ref}</span>
            </p>
            <Button
              onClick={() => onOpenChange(false)}
              className="mt-4 bg-emerald-600 text-white hover:bg-emerald-700 rounded-xl"
              data-testid="pay-now-done-btn"
            >
              Done
            </Button>
          </div>
        )}

        {!loading && !done && data && !eligible && (
          <div
            className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700 flex items-start gap-2"
            data-testid="pay-now-blocked"
          >
            <Truck className="h-4 w-4 mt-0.5 flex-shrink-0 text-slate-500" />
            <div>
              <p className="font-semibold text-slate-800">Not available right now</p>
              <p className="text-xs text-slate-600 mt-0.5">{blockedReason || "This order can't be paid online anymore."}</p>
            </div>
          </div>
        )}

        {!loading && !done && data && eligible && (
          <>
            <div
              className="rounded-2xl border border-amber-200 bg-amber-50 p-4"
              data-testid="pay-now-amount-card"
            >
              <p className="text-xs font-bold uppercase tracking-wider text-amber-700">
                Order total
              </p>
              <p className="text-3xl font-extrabold text-amber-900" data-testid="pay-now-amount">
                ₹{amount}
              </p>
              <p className="text-xs text-amber-700 mt-1">
                {flow === "cod_to_prepaid"
                  ? "We'll switch the order from COD to Prepaid once payment completes."
                  : "Complete this payment to confirm your booking."}
              </p>
            </div>

            {err && (
              <div
                className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 flex items-start gap-2"
                data-testid="pay-now-error"
              >
                <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span>{err}</span>
              </div>
            )}

            <button
              type="button"
              onClick={payWithWallet}
              disabled={!walletSufficient || busy !== null}
              className={`w-full rounded-2xl border-2 p-4 flex items-center justify-between gap-3 text-left transition ${
                walletSufficient
                  ? "border-emerald-200 bg-emerald-50 hover:border-emerald-400 hover:shadow-sm"
                  : "border-slate-200 bg-slate-50 opacity-60 cursor-not-allowed"
              }`}
              data-testid="pay-now-wallet-btn"
            >
              <div className="flex items-center gap-3">
                <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${
                  walletSufficient ? "bg-emerald-100" : "bg-slate-200"
                }`}>
                  <Wallet className={`h-5 w-5 ${walletSufficient ? "text-emerald-700" : "text-slate-500"}`} />
                </div>
                <div>
                  <p className="font-bold text-slate-900">PickNGo Wallet</p>
                  <p className="text-xs text-slate-600">
                    Balance: <span className="font-semibold">₹{walletBalance}</span>
                    {!walletSufficient && amount > 0 && (
                      <span className="ml-1 text-red-600">— short by ₹{amount - walletBalance}</span>
                    )}
                  </p>
                </div>
              </div>
              {busy === "wallet" ? (
                <Loader2 className="h-5 w-5 animate-spin text-emerald-600" />
              ) : (
                <span className="text-xs font-bold text-emerald-700 whitespace-nowrap">
                  Pay ₹{amount} instantly
                </span>
              )}
            </button>

            <button
              type="button"
              onClick={payWithRazorpay}
              disabled={!razorpayEnabled || busy !== null}
              className={`w-full rounded-2xl border-2 p-4 flex items-center justify-between gap-3 text-left transition ${
                razorpayEnabled
                  ? "border-orange-200 bg-orange-50/50 hover:border-orange-400 hover:shadow-sm"
                  : "border-slate-200 bg-slate-50 opacity-60 cursor-not-allowed"
              }`}
              data-testid="pay-now-razorpay-btn"
            >
              <div className="flex items-center gap-3">
                <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${
                  razorpayEnabled ? "bg-orange-100" : "bg-slate-200"
                }`}>
                  <CreditCard className={`h-5 w-5 ${razorpayEnabled ? "text-[#FF6B00]" : "text-slate-500"}`} />
                </div>
                <div>
                  <p className="font-bold text-slate-900">Pay online (Razorpay)</p>
                  <p className="text-xs text-slate-600">
                    UPI · Cards · Net banking
                    {!razorpayEnabled && (
                      <span className="ml-1 text-red-600">— unavailable</span>
                    )}
                  </p>
                </div>
              </div>
              {busy === "razorpay" ? (
                <Loader2 className="h-5 w-5 animate-spin text-[#FF6B00]" />
              ) : (
                <span className="text-xs font-bold text-[#FF6B00] whitespace-nowrap">
                  Pay ₹{amount} →
                </span>
              )}
            </button>

            <div className="text-[11px] text-slate-500 flex items-center gap-1.5 mt-1">
              <ShieldCheck className="h-3 w-3" />
              Payments are processed securely. You'll see the new status instantly.
            </div>
          </>
        )}

        {!loading && err && !data && (
          <div className="p-4 rounded-lg border border-red-200 bg-red-50 text-sm text-red-700">
            {err}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
